import { collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '../../firebase';
import { MeetingNote } from '../../types';
import { chunkIds, FIRESTORE_IN_LIMIT } from '../../utils/firestoreChunks';
import { parseMeetingNoteDoc } from '../MeetingNotes/meetingNotesUtils';

/**
 * All meeting notes for the given cases, grouped by caseId.
 *
 * Chunked `in` queries rather than reading the collection and filtering in memory: billing is
 * per document returned, so fetching only the visible cases' notes costs what it uses, while a
 * full read pays for every other counselor's notes just to discard them.
 */
export async function loadNotesByCaseIds(
  caseIds: string[]
): Promise<Map<string, MeetingNote[]>> {
  const byCaseId = new Map<string, MeetingNote[]>();
  const uniqueIds = Array.from(new Set(caseIds.filter(Boolean)));
  if (uniqueIds.length === 0) return byCaseId;

  for (const idChunk of chunkIds(uniqueIds, FIRESTORE_IN_LIMIT)) {
    const snapshot = await getDocs(
      query(collection(db, 'meetingNotes'), where('caseId', 'in', idChunk))
    );
    snapshot.forEach((noteDoc) => {
      const note = parseMeetingNoteDoc(noteDoc.id, noteDoc.data());
      if (!note.caseId) return;
      const list = byCaseId.get(note.caseId);
      if (list) list.push(note);
      else byCaseId.set(note.caseId, [note]);
    });
  }

  return byCaseId;
}

export interface CaseNotesSummaries {
  /** Latest note's text per caseId, for the case list's preview line. */
  latestContent: Record<string, string>;
  /** Session numbers that already have a tablou, per caseId — for the session road's dot. */
  tabloulSessionsByCaseId: Record<string, Set<number>>;
}

/**
 * Latest meeting note content, and which sessions already have a tablou, per caseId — one
 * chunked `in` query (max 10) either way, so reading both here costs nothing beyond what the
 * latest-note preview already paid for; the difference is only in what the pass over the results
 * keeps.
 */
export async function loadLatestNotesByCaseIds(
  caseIds: string[]
): Promise<CaseNotesSummaries> {
  const uniqueIds = Array.from(new Set(caseIds.filter(Boolean)));
  if (uniqueIds.length === 0) {
    return { latestContent: {}, tabloulSessionsByCaseId: {} };
  }

  const latestByCase = new Map<string, MeetingNote>();
  const tabloulSessionsByCase = new Map<string, Set<number>>();

  for (const idChunk of chunkIds(uniqueIds, FIRESTORE_IN_LIMIT)) {
    const notesQuery = query(
      collection(db, 'meetingNotes'),
      where('caseId', 'in', idChunk)
    );
    const snapshot = await getDocs(notesQuery);
    snapshot.forEach((noteDoc) => {
      const note = parseMeetingNoteDoc(noteDoc.id, noteDoc.data());
      if (!note.caseId) return;

      const existing = latestByCase.get(note.caseId);
      if (!existing || note.createdAt > existing.createdAt) {
        latestByCase.set(note.caseId, note);
      }

      if (note.tablou && note.sessionNumber != null) {
        const sessions = tabloulSessionsByCase.get(note.caseId) ?? new Set<number>();
        sessions.add(note.sessionNumber);
        tabloulSessionsByCase.set(note.caseId, sessions);
      }
    });
  }

  const latestContent: Record<string, string> = {};
  const tabloulSessionsByCaseId: Record<string, Set<number>> = {};
  uniqueIds.forEach((caseId) => {
    latestContent[caseId] = latestByCase.get(caseId)?.content ?? '';
    tabloulSessionsByCaseId[caseId] = tabloulSessionsByCase.get(caseId) ?? new Set();
  });

  return { latestContent, tabloulSessionsByCaseId };
}
